import axiosService from './axios';
import { CityPlace, isSearchableCityQuery } from './cityPlace';

export type { CityPlace } from './cityPlace';
export { citySelectionFromText, emptyCityPlace, isSearchableCityQuery } from './cityPlace';

let sessionToken = createSession();
let sessionPrefix = '';

function createSession(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `city-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function sessionFor(query: string): string {
    const normalized = query.trim().toLowerCase();
    if (!sessionPrefix || !normalized.startsWith(sessionPrefix)) {
        sessionToken = createSession();
    }
    sessionPrefix = normalized;
    return sessionToken;
}

function isAbort(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;
    const code = (error as { code?: string }).code;
    return code === 'ERR_CANCELED';
}

export async function searchCities(
    query: string,
    options?: { limit?: number; signal?: AbortSignal },
): Promise<CityPlace[]> {
    const trimmed = query.trim();
    if (!isSearchableCityQuery(trimmed)) return [];
    try {
        const response = await axiosService.get('/location/autocomplete', {
            signal: options?.signal,
            params: {
                input: trimmed,
                limit: options?.limit,
                session: sessionFor(trimmed),
            },
        });
        const places = response?.data?.places;
        return Array.isArray(places) ? places : [];
    } catch (error) {
        if (isAbort(error)) return [];
        const status = (error as { response?: { status?: number } }).response?.status;
        console.error('City lookup failed', status || 'request_failed');
        return [];
    }
}

// One Place Details call for the city the member picked, using the session the
// suggestions were fetched with. A failure keeps the suggestion, without coordinates.
export async function selectCity(place: CityPlace): Promise<CityPlace> {
    if (!place?.placeId) return place;
    const session = sessionToken;
    sessionPrefix = '';
    try {
        const response = await axiosService.get('/location/place', {
            params: { placeId: place.placeId, session },
        });
        const resolved = response?.data?.place;
        if (!resolved || typeof resolved !== 'object') return place;
        return {
            ...place,
            city: resolved.city || place.city,
            region: resolved.region || place.region,
            country: place.country || resolved.country,
            lat: typeof resolved.lat === 'number' ? resolved.lat : null,
            lng: typeof resolved.lng === 'number' ? resolved.lng : null,
        };
    } catch (error) {
        if (!isAbort(error)) {
            const status = (error as { response?: { status?: number } }).response?.status;
            console.error('City details failed', status || 'request_failed');
        }
        return place;
    }
}
